import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import {
    X,
    Pause,
    Play,
    Headphones,
    AlertCircle,
    PencilLine,
} from "lucide-react";

import { BillingBlockedAlert } from "~/components/billing/billing-blocked-alert";
import { Button } from "~/components/ui/button";
import { meditationDebugLog } from "~/features/meditations/utils/browser-debug";
import { useUserDetails } from "~/hooks/queries/use-user-details";
import { trackEvent } from "~/lib/analytics";
import { useStreamingMeditation } from "../hooks/use-streaming-meditation";
import type {
    MeditationOpening,
    MeditationPauseMultiplier,
    MeditationType,
} from "~/features/meditations/types";
import { BreathingAnimation } from "./breathing-animation";
import {
    MeditationCapturePanel,
    type CaptureField,
    type CaptureKind,
} from "./meditation-capture-panel";
import { MeditationBufferingIndicator } from "./meditation-buffering-indicator";
import { MeditationHistogramPlayer } from "./meditation-histogram-player";

// AIDEV-NOTE: Fullscreen streaming meditation player: breathing → playback with an in-meditation
// capture rail at the right (desktop) / a Journal drawer (mobile). The rail is config-driven via
// MeditationCapturePanel — general meditations get a "Reflect" journal (meditation_journal),
// DPMP resource exercises pass pcc_card / note / name with captureFields + capturePartId. Audio
// generation starts on mount so there's minimal wait; focusing a capture field pauses the audio.

type Props = {
    type: MeditationType;
    voice: string;
    pauseMultiplier?: MeditationPauseMultiplier;
    opening?: MeditationOpening;
    sessionId?: string;
    partInteractionId?: string;
    sessionToken?: string;
    title?: string;
    // AIDEV-NOTE: Overrides the title persisted by the capture rail (pcc_card entry title)
    // without changing the visible `title` header. Defaults to `title` when omitted.
    captureTitle?: string;
    partName?: string; // AIDEV-NOTE: Part name for part meditations
    // AIDEV-NOTE: Selected part's generated image (signed URL); shown as the player icon when present.
    partImageUrl?: string;
    // AIDEV-NOTE: Resource-contextual meditation label; overrides MEDITATION_TYPE_LABELS[type].
    meditationTypeLabel?: string;
    onClose?: () => void;
    // AIDEV-NOTE: Force fallback mode for testing (e.g., ?forceFallback=1 URL param)
    forceFallback?: boolean;
    // AIDEV-NOTE: Trial flag for unified HLS playback in non-Safari browsers.
    forceHlsJs?: boolean;
    // AIDEV-NOTE: Pass meditationId to stream a pre-cached meditation by ID.
    // This enables fullscreen playback for existing meditations without re-generation.
    meditationId?: string;
    // AIDEV-NOTE: Override the SSE stream URL (e.g. for resource exercises with experienceId).
    // When provided WITHOUT experienceId, the hook skips HLS and uses SSE directly.
    streamUrl?: string;
    // AIDEV-NOTE: EVO-1954 — exercise generation params; when set, exercises
    // use the same durable HLS bootstrap as every other meditation type.
    experienceId?: string;
    selectedPartIds?: string[];
    // AIDEV-NOTE: EVO-2476 — approximate total (seconds) for verbatim-script exercises, shown
    // as "~N min" while a live generation's exact total is still unknown.
    estimatedTotalSeconds?: number | null;
    // AIDEV-NOTE: In-meditation capture rail config. Defaults to a "Reflect" journal
    // (meditation_journal) for general meditations; resource exercises pass pcc_card / note / name
    // with captureFields + capturePartId (the selected part).
    captureKind?: CaptureKind;
    captureFields?: CaptureField[];
    capturePartId?: string;
    // AIDEV-NOTE: The selected part's inferred type — drives which type-conditional PCC fields
    // initially render in the capture rail (never auto-saved).
    capturePartType?: string | null;
    // AIDEV-NOTE: Fired after an in-player rename so the host can update its part list.
    onRename?: (partId: string, newName: string) => void;
    // AIDEV-NOTE: The meditation setup overlay fires "streaming meditation started" itself
    // (with meditationKind + pauseMultiplier) before mounting an exercise-backed player, so
    // it passes true to suppress the exercise-side event below and avoid double counting.
    startedEventTracked?: boolean;
};

// AIDEV-NOTE: Human-readable labels for meditation types
const MEDITATION_TYPE_LABELS: Record<MeditationType, string> = {
    intro: "Introduction Meditation",
    session: "Session Meditation",
    path: "Path Meditation",
    part: "Part Check-In",
};

export function FullscreenStreamingPlayer({
    type,
    voice,
    pauseMultiplier = 1,
    opening = "full",
    sessionId,
    partInteractionId,
    sessionToken,
    title = "Meditation",
    captureTitle,
    partName,
    partImageUrl,
    meditationTypeLabel,
    onClose,
    forceFallback,
    forceHlsJs,
    meditationId,
    streamUrl,
    experienceId,
    selectedPartIds,
    estimatedTotalSeconds,
    captureKind,
    captureFields,
    capturePartId,
    capturePartType,
    onRename,
    startedEventTracked = false,
}: Props) {
    const player = useStreamingMeditation({
        type,
        voice,
        pauseMultiplier,
        opening,
        sessionId,
        partInteractionId,
        sessionToken,
        forceFallback,
        forceHlsJs,
        meditationId,
        streamUrl,
        experienceId,
        selectedPartIds,
    });

    // AIDEV-NOTE: Auto-start on mount. start() both generates and plays. If the browser rejects
    // play() with NotAllowedError (mobile autoplay policy), the needsUserGesture fallback shows
    // "Tap to listen" for a fresh gesture.
    const hasAutoStartedRef = useRef(false);
    useEffect(() => {
        if (
            !hasAutoStartedRef.current &&
            player.phase === "idle" &&
            !player.isLoading
        ) {
            hasAutoStartedRef.current = true;
            setTimeout(() => {
                player.start();
            }, 0);
        }
    }, [player.phase, player.isLoading, player.start]);

    useEffect(() => {
        meditationDebugLog("[FullscreenStreamingPlayer] flow state changed", {
            playerPhase: player.phase,
            isLoading: player.isLoading,
            isGenerating: player.isGenerating,
            isPlaying: player.isPlaying,
            error: player.error,
            forceFallback: forceFallback ?? false,
            forceHlsJs: forceHlsJs ?? false,
            type,
            voice,
            pauseMultiplier,
        });
    }, [
        forceFallback,
        forceHlsJs,
        player.error,
        player.isGenerating,
        player.isLoading,
        player.isPlaying,
        player.phase,
        type,
        voice,
        pauseMultiplier,
    ]);

    // AIDEV-NOTE: Resource exercises ride the player as type="part" (generation
    // machinery), but analytics must report them as "exercise" — experienceId
    // is the discriminator.
    const analyticsType = experienceId ? ("exercise" as const) : type;

    // AIDEV-NOTE: Provenance stamped on everything the in-player capture rail writes — the rail is
    // the same component the resource page mounts inline, so the surface has to come from the host.
    // Memoized so the rail's save callbacks aren't rebuilt every render.
    const captureProvenance = useMemo(
        () => ({
            source: "meditation_player" as const,
            ...(experienceId ? { experienceId } : {}),
        }),
        [experienceId],
    );

    // AIDEV-NOTE: Exercises have no launcher-side "streaming meditation started"
    // (other surfaces fire it at their play buttons) — fire it here once on
    // actual playback start so resource plays are countable in analytics.
    const startedFiredRef = useRef(false);
    useEffect(() => {
        if (
            experienceId &&
            !startedEventTracked &&
            player.isPlaying &&
            !startedFiredRef.current
        ) {
            startedFiredRef.current = true;
            trackEvent("streaming meditation started", {
                type: analyticsType,
                voice,
            });
        }
    }, [
        experienceId,
        startedEventTracked,
        player.isPlaying,
        analyticsType,
        voice,
    ]);

    // AIDEV-NOTE: Track meditation completion once for analytics.
    const finishedFiredRef = useRef(false);
    useEffect(() => {
        if (player.phase === "complete" && !finishedFiredRef.current) {
            finishedFiredRef.current = true;
            trackEvent("meditation finished", {
                type: analyticsType,
                sessionId,
                partId: partInteractionId,
            });
        }
    }, [player.phase, analyticsType, sessionId, partInteractionId]);

    // AIDEV-NOTE: Create a dummy audio ref for histogram player (it doesn't use it for streaming)
    const dummyAudioRef = useRef<HTMLAudioElement>(null);

    // AIDEV-NOTE: Capture is never shown during playback. Generated meditations
    // enter it on natural completion or Finish; resources remain Finish-only.
    // The X always exits directly and nothing persists unless the user saves.
    const [stage, setStage] = useState<"playing" | "capture">("playing");

    const formatTime = (sec: number) => {
        if (!isFinite(sec) || sec < 0) return "0:00";
        const m = Math.floor(sec / 60);
        const s = Math.floor(sec % 60)
            .toString()
            .padStart(2, "0");
        return `${m}:${s}`;
    };

    const handleClose = () => {
        player.stop();
        onClose?.();
    };

    // AIDEV-NOTE: Debounce play/pause toggle to prevent double-fire from Button's
    // onClick + onTouchEnd both triggering on touch devices. Without this, tap fires
    // touchend (pause) then click (play) — the second call reads updated state and reverses.
    const lastToggleRef = useRef(0);
    const togglePlayPause = useCallback(() => {
        const now = Date.now();
        if (now - lastToggleRef.current < 100) return;
        lastToggleRef.current = now;

        if (player.isPlaying) {
            player.pause();
        } else {
            player.play();
        }
    }, [player.isPlaying, player.pause, player.play]);

    // AIDEV-NOTE: Spacebar toggles play/pause so users don't break meditative focus to find the
    // pause button. Ignore it while a capture field is focused so space types normally.
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            if (
                target &&
                (target.tagName === "INPUT" ||
                    target.tagName === "TEXTAREA" ||
                    target.isContentEditable)
            ) {
                return;
            }
            if (e.code === "Space" || e.key === " ") {
                e.preventDefault();
                togglePlayPause();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [togglePlayPause]);

    const visualizationMode = getFullscreenMeditationVisualizationMode({
        error: player.error,
        phase: player.phase,
        currentTime: player.currentTime,
        audiblePlaybackStartSeconds: player.audiblePlaybackStartSeconds,
        awaitingAudiblePlaybackStart: player.awaitingAudiblePlaybackStart,
    });
    const showBreathing = visualizationMode === "breathing";
    const showHistogram = visualizationMode === "histogram";
    // AIDEV-NOTE: EVO-1954 — during live HLS generation the total is genuinely
    // unknown (the script is still being written), so the timecode shows
    // elapsed-only until DURATION_DISCOVERED lands (playlist finalized, ~1-3
    // min into playback). mainDuration is only ever set from a FINAL duration
    // (SSE complete event or discovery), so it alone gates the total — the old
    // fully-buffered gate kept a known total hidden for most of the listen.
    const shouldShowTotalDuration =
        !player.awaitingAudiblePlaybackStart && player.mainDuration > 0;
    // AIDEV-NOTE: EVO-2476 — cached replays know the total at once but fresh generations
    // don't, so the total appeared only sometimes. Bridge the gap with the caller's estimate.
    const approxTotalLabel =
        !shouldShowTotalDuration &&
        !player.awaitingAudiblePlaybackStart &&
        estimatedTotalSeconds &&
        estimatedTotalSeconds > 0
            ? `~${Math.max(1, Math.round(estimatedTotalSeconds / 60))} min`
            : null;

    useEffect(() => {
        // AIDEV-NOTE: Safari HLS debugging depends on seeing the exact fullscreen
        // mode decision next to the playback clock and audible threshold.
        meditationDebugLog(
            "[FullscreenStreamingPlayer] visualization mode changed",
            {
                mode: visualizationMode,
                phase: player.phase,
                currentTime: player.currentTime,
                audiblePlaybackStartSeconds: player.audiblePlaybackStartSeconds,
                awaitingAudiblePlaybackStart:
                    player.awaitingAudiblePlaybackStart,
                isLoading: player.isLoading,
                isGenerating: player.isGenerating,
                isPlaying: player.isPlaying,
                error: player.error,
                source: player.source,
                voice,
                type,
            },
        );
    }, [
        visualizationMode,
        player.phase,
        player.currentTime,
        player.audiblePlaybackStartSeconds,
        player.awaitingAudiblePlaybackStart,
        player.isLoading,
        player.isGenerating,
        player.isPlaying,
        player.error,
        player.source,
        voice,
        type,
    ]);

    // AIDEV-NOTE: Generated meditations auto-open their journal on natural
    // completion and do not use the legacy try-resources gate. Resource/DPMP
    // captures retain their existing gated, Finish-only behavior.
    const { data: userDetails } = useUserDetails();
    const tryResourcesEnabled =
        // AIDEV-NOTE: `featureFlags` must be optional-chained too — a user payload without
        // it (anonymous/partial cache) threw here and took the whole player down mid-play.
        userDetails?.featureFlags?.["try-resources"] === true;
    const kind: CaptureKind = captureKind ?? "meditation_journal";
    const isGeneratedMeditationJournal =
        !experienceId &&
        kind === "meditation_journal" &&
        Boolean(sessionId || partInteractionId);
    // AIDEV-NOTE: A resource exercise has neither sessionId nor partInteractionId, so an
    // experience that explicitly configures captureKind="meditation_journal" used to render
    // nothing at all. experienceId now anchors it (the journal route resolves the meditation row
    // by userId+experienceId). Deliberately scoped to the EXPLICIT config — the implicit default
    // (captureKind undefined) stays as it was, so no exercise gains a Reflect step it wasn't
    // configured for.
    const hasCapture =
        kind === "meditation_journal"
            ? isGeneratedMeditationJournal ||
              (tryResourcesEnabled &&
                  Boolean(
                      sessionId ||
                          partInteractionId ||
                          (experienceId &&
                              captureKind === "meditation_journal"),
                  ))
            : Boolean(capturePartId);

    // AIDEV-NOTE: Finish stops audio and reveals capture. The X exits without capture;
    // natural completion below auto-reveals it only for generated meditations.
    const enterCapture = () => {
        player.stop();
        setStage("capture");
    };

    useEffect(() => {
        if (
            player.phase === "complete" &&
            stage === "playing" &&
            isGeneratedMeditationJournal
        ) {
            setStage("capture");
        }
    }, [player.phase, stage, isGeneratedMeditationJournal]);

    const captureCard = hasCapture ? (
        <div className="flex flex-col gap-5">
            {/* AIDEV-NOTE: onClose turns the panel's Save into "Save & Close" — it persists then
                dismisses the overlay, replacing the old separate "Finished" button. */}
            <MeditationCapturePanel
                captureKind={kind}
                title={captureTitle ?? title}
                fields={captureFields}
                partId={capturePartId}
                partName={partName}
                partType={capturePartType}
                sessionId={sessionId}
                partInteractionId={partInteractionId}
                meditationType={type}
                meditationId={player.activeMeditationId ?? meditationId}
                onFieldFocus={player.pause}
                onRename={onRename}
                onClose={handleClose}
                onSkip={kind === "meditation_journal" ? handleClose : undefined}
                provenance={captureProvenance}
            />
        </div>
    ) : null;

    // AIDEV-NOTE: Render overlay via portal to document.body to escape layout stacking contexts.
    // The #layout-body has overflow-hidden and the header has backdrop-blur-sm which create
    // stacking contexts that prevent fixed inset-0 from covering the full viewport.
    let overlayContent: React.ReactNode;

    // Error state
    if (player.error) {
        const isBillingReadOnly =
            player.billingBlocked?.thresholdState === "blocked";
        overlayContent = (
            <div className="fixed inset-0 z-50 bg-stone-50 flex flex-col items-center justify-center px-4 sm:px-8">
                {/* Close button */}
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 sm:top-6 sm:right-6 text-gray-600 hover:text-gray-900 transition-colors p-2 touch-manipulation"
                    aria-label="Close"
                >
                    <X className="h-7 w-7 sm:h-6 sm:w-6 stroke-2" />
                </button>

                <div className="flex max-w-xl flex-col items-center gap-4 text-center">
                    {player.billingBlocked ? (
                        <BillingBlockedAlert billing={player.billingBlocked} />
                    ) : (
                        <>
                            <div className="rounded-full bg-amber-50 p-4">
                                <AlertCircle className="size-8 text-amber-600" />
                            </div>
                            <p className="text-lg font-medium text-amber-700">
                                {player.error}
                            </p>
                        </>
                    )}
                    <div className="flex gap-2">
                        {!isBillingReadOnly && (
                            <Button onClick={player.start} variant="outline">
                                Retry
                            </Button>
                        )}
                        <Button onClick={handleClose} variant="ghost">
                            Close
                        </Button>
                    </div>
                </div>
            </div>
        );
    } else {
        // AIDEV-NOTE: Playback content (breathing / tap-to-listen / histogram) — lives in the left
        // pane. The capture rail sits to its right (desktop) or in a drawer (mobile).
        const meditationContent = (
            <>
                {/* Breathing animation during loading - no title shown here */}
                {showBreathing &&
                    !showHistogram &&
                    !player.needsUserGesture && <BreathingAnimation />}

                {/* AIDEV-NOTE: Autoplay policy blocked play() after start() was called. On mobile,
                    the gesture context from opening the player expires during HLS buffering, so
                    play() gets NotAllowedError. Show "Tap to listen" for a fresh gesture. */}
                {player.needsUserGesture && (
                    <div className="flex flex-col items-center gap-6">
                        <div className="rounded-full bg-muted p-4">
                            <Headphones className="size-8 text-muted-foreground" />
                        </div>
                        <p className="text-lg font-medium text-muted-foreground">
                            Your meditation is ready
                        </p>
                        <Button
                            onClick={player.resumePlayback}
                            size="lg"
                            className="gap-2 touch-manipulation"
                        >
                            <Play className="size-5" />
                            Tap to listen
                        </Button>
                    </div>
                )}

                {/* Histogram player once audio starts */}
                {/* AIDEV-NOTE: Skip histogram when needsUserGesture is true to avoid rendering two play UIs */}
                {showHistogram && !player.needsUserGesture && (
                    <>
                        {/* AIDEV-NOTE: Player icon — the selected part's generated image when ready,
                            else the generic Headphones glyph. */}
                        <div className="mb-6 sm:mb-8">
                            <div className="flex size-24 sm:size-32 items-center justify-center overflow-hidden rounded-2xl bg-muted">
                                {partImageUrl ? (
                                    <img
                                        src={partImageUrl}
                                        alt={partName ?? "Part"}
                                        className="size-full object-cover"
                                    />
                                ) : (
                                    <Headphones className="size-12 sm:size-16 text-muted-foreground" />
                                )}
                            </div>
                        </div>

                        {/* AIDEV-NOTE: Part name is the title, meditation type the subtitle (no status
                            line). Falls back to the type label when there's no part name. */}
                        <div className="mb-6 sm:mb-8 text-center px-4">
                            <h2 className="text-xl sm:text-2xl font-semibold">
                                {partName ??
                                    meditationTypeLabel ??
                                    MEDITATION_TYPE_LABELS[type]}
                            </h2>
                            {partName && (
                                <p className="mt-1 text-lg text-muted-foreground">
                                    {meditationTypeLabel ??
                                        MEDITATION_TYPE_LABELS[type]}
                                </p>
                            )}
                        </div>

                        {/* Histogram bar - edge to edge */}
                        <div className="w-full max-w-4xl">
                            <MeditationHistogramPlayer
                                isPlaying={player.isPlaying}
                                currentTime={player.currentTime}
                                duration={
                                    player.totalDuration ||
                                    player.introDuration ||
                                    0
                                }
                                title={title}
                                onPlay={player.play}
                                onPause={player.pause}
                                audioRef={dummyAudioRef}
                            />
                        </div>

                        {/* AIDEV-NOTE: Buffering spinner (Margie incident) — distinguishes
                            data-starved playback from intentional meditation silence.
                            EVO-1813: isStalled is debounced ~2s in StallDetector. */}
                        <MeditationBufferingIndicator
                            isBuffering={player.isStalled}
                            isOffline={player.isOffline}
                            slowNetworkDetected={player.slowNetworkDetected}
                            className="mt-4"
                        />

                        {/* Controls below histogram - play/pause and timecode */}
                        <div className="mt-6 sm:mt-8 flex flex-col items-center gap-3 sm:gap-4">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={togglePlayPause}
                                disabled={
                                    player.isLoading ||
                                    (player.phase === "gap" &&
                                        !player.mainDuration)
                                }
                                className="!h-16 !w-16 sm:!h-20 sm:!w-20 touch-manipulation active:scale-95 [&_svg]:!size-8 sm:[&_svg]:!size-10"
                            >
                                {player.isPlaying ? <Pause /> : <Play />}
                            </Button>

                            {/* Timecode — elapsed-only until the final total
                                is known; never render a "/ ..." placeholder */}
                            <div className="flex items-center gap-2 text-sm sm:text-base text-muted-foreground">
                                <span>{formatTime(player.currentTime)}</span>
                                {shouldShowTotalDuration ? (
                                    <>
                                        <span>/</span>
                                        <span>
                                            {formatTime(player.totalDuration)}
                                        </span>
                                    </>
                                ) : approxTotalLabel ? (
                                    <>
                                        <span>/</span>
                                        <span>{approxTotalLabel}</span>
                                    </>
                                ) : null}
                            </div>

                            {/* AIDEV-NOTE: Keyboard shortcut hint — hidden on touch-only devices via hidden/sm:block */}
                            <p className="hidden sm:block text-xs text-muted-foreground/50">
                                Press{" "}
                                <kbd className="rounded border border-muted-foreground/20 px-1.5 py-0.5 font-mono text-[10px]">
                                    space
                                </kbd>{" "}
                                to {player.isPlaying ? "pause" : "resume"}
                            </p>
                        </div>
                    </>
                )}
            </>
        );

        overlayContent = (
            <div className="fixed inset-0 z-50 flex flex-col bg-stone-50">
                {/* AIDEV-NOTE: Top-right X always exits the whole player — it never routes
                    through the capture step (Michelle Glass, Jul 7 2026). Finish is the only
                    path to capture. */}
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 z-20 p-2 text-gray-600 transition-colors touch-manipulation hover:text-gray-900 sm:top-6 sm:right-6"
                    aria-label="Exit fullscreen"
                >
                    <X className="h-7 w-7 stroke-2 sm:h-6 sm:w-6" />
                </button>

                {stage === "playing" ? (
                    <div className="relative flex flex-1 flex-col items-center justify-center px-4 py-16 sm:px-8">
                        {meditationContent}

                        {/* Finish → stop audio and show the (skippable) capture card.
                            AIDEV-NOTE: Hidden until audio is actually playing — while the
                            breathing loader is up (generating / buffering) or the
                            "Tap to listen" gesture prompt shows, there is nothing to
                            finish yet (Brent, 2026-09-09). */}
                        {hasCapture && showHistogram && (
                            <Button
                                size="lg"
                                variant="outline"
                                onClick={enterCapture}
                                className="mt-10 gap-2"
                            >
                                <PencilLine className="size-4" />
                                Finish
                            </Button>
                        )}
                    </div>
                ) : (
                    // AIDEV-NOTE: Capture pane — same single column the meditation used. `my-auto`
                    // centers the card when it fits and collapses to top-aligned + scrollable when
                    // taller than the viewport (QA: A2, Jul 2026).
                    <div className="relative flex flex-1 flex-col items-center overflow-y-auto px-4 py-16 sm:px-8">
                        <div className="my-auto w-full max-w-2xl">
                            {captureCard}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // AIDEV-NOTE: Portal to document.body escapes all parent stacking contexts
    if (typeof document !== "undefined") {
        return createPortal(overlayContent, document.body);
    }
    return overlayContent;
}

export function getFullscreenMeditationVisualizationMode(player: {
    error: string | null;
    phase: "idle" | "intro" | "gap" | "main" | "complete";
    currentTime: number;
    audiblePlaybackStartSeconds: number;
    awaitingAudiblePlaybackStart: boolean;
}) {
    if (player.error) {
        return "none";
    }

    // AIDEV-NOTE: Native HLS fallback must not leave breathing until the hook
    // explicitly confirms audible playback has begun. This guards against
    // transient phase/currentTime updates outrunning actual Safari audio.
    if (
        player.phase === "idle" ||
        player.awaitingAudiblePlaybackStart ||
        player.currentTime < player.audiblePlaybackStartSeconds
    ) {
        return "breathing";
    }

    return "histogram";
}
